/**
 * Timed steps (2026-10-19, Jens): the countdown on a step ("Plank – 0:45"). Pure functions on a
 * small state, all measured against the clock (`now`), so the time stays right when the phone
 * locks or the app is in the background and the screen stops updating.
 */
export const STEP_TIMER_MAX_SECONDS = 4 * 60 * 60;

export type StepTimer = {
  totalMs: number;
  /** Time left while paused or not started. */
  leftMs: number;
  /** When it reaches zero while running; null while paused or not started. */
  endsAt: number | null;
};

export const newStepTimer = (seconds: number): StepTimer => ({ totalMs: seconds * 1000, leftMs: seconds * 1000, endsAt: null });

export const remainingMs = (t: StepTimer, now: number): number =>
  t.endsAt === null ? t.leftMs : Math.max(0, t.endsAt - now);

export const isRunning = (t: StepTimer, now: number): boolean => t.endsAt !== null && t.endsAt > now;
export const isFinished = (t: StepTimer, now: number): boolean => remainingMs(t, now) <= 0;
export const isStarted = (t: StepTimer): boolean => t.endsAt !== null || t.leftMs < t.totalMs;

export function startTimer(t: StepTimer, now: number): StepTimer {
  if (t.endsAt !== null || t.leftMs <= 0) return t;
  return { ...t, endsAt: now + t.leftMs };
}

export function pauseTimer(t: StepTimer, now: number): StepTimer {
  if (t.endsAt === null) return t;
  return { ...t, leftMs: remainingMs(t, now), endsAt: null };
}

export const resetTimer = (t: StepTimer): StepTimer => ({ ...t, leftMs: t.totalMs, endsAt: null });

/** "0:45", "12:00", "1:05:00" — whole seconds, rounded up so it shows 0:00 only at the end. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** For the editor: minutes and seconds of a step's timer. */
export function splitSeconds(total: number | null | undefined): { minutes: number; seconds: number } {
  const t = Math.max(0, Math.round(Number(total) || 0));
  return { minutes: Math.floor(t / 60), seconds: t % 60 };
}

/** From the editor's minutes and seconds: the timer in seconds, or null for no timer. */
export function joinSeconds(minutes: unknown, seconds: unknown): number | null {
  const m = Math.max(0, Math.floor(Number(minutes) || 0));
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const total = m * 60 + s;
  return total > 0 ? Math.min(total, STEP_TIMER_MAX_SECONDS) : null;
}
